import {
  Avatar,
  Box,
  Card,
  CardHeader,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
} from '@mui/material';
import {
  Brightness4,
  LightMode,
  Logout,
  ViewSidebar,
} from '@mui/icons-material';
import { ReactElement, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMuiSwitchableTheme } from '../../theme';
import { TruncatedLabel } from './truncated-label';

type BaseDropdownItem = {
  label: string;
  icon: ReactElement;
};

type FunctionDropdownItem = BaseDropdownItem & {
  onClick: () => void;
};

type LinkDropdownItem = BaseDropdownItem & {
  href: string;
};

type DropDownItem = FunctionDropdownItem | LinkDropdownItem;

export interface AuthProps<TUser = unknown> {
  user: TUser;
  logoutFn: () => Promise<void>;
  vendorName: string;
  dropdownItems?: DropDownItem[];
}

export function Auth<
  TUser extends { first_name?: string; picture_url?: string | null },
>({ user, logoutFn, vendorName, dropdownItems = [] }: AuthProps<TUser>) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const {
    navMode,
    navCollapsed,
    setNavCollapsed,
    navWidth,
    setMuiTheme,
    themeMode,
    setNavMode,
    isTablet,
  } = useMuiSwitchableTheme();

  const collapsed = useMemo(
    () => navMode === 'left' && navCollapsed,
    [navMode, navCollapsed],
  );

  // Picking something from this menu also closes the nav drawer it lives in.
  // The drawer's own links do that through their click handler; these items
  // only closed the menu, so on a phone choosing Profile navigated underneath
  // a drawer that stayed open on top of it. Only the temporary drawer
  // (`navMode === 'top'`) is closed — the pinned desktop rail is left alone.
  const closeMenuAndDrawer = () => {
    setOpen(false);
    if (navMode === 'top') setNavCollapsed(true);
  };

  // The menu drops down from the card only where the card is at the top of the
  // screen: the desktop top bar. Below that width the card lives at the bottom
  // of the nav drawer, where "down" has no room and the menu ended up covering
  // the card that opened it.
  const opensDownward = navMode === 'top' && !isTablet;

  return (
    <Box>
      <Card
        onClick={() => setOpen(true)}
        sx={{
          cursor: 'pointer',
          overflow: 'hidden',
          backgroundColor: 'transparent',
          backdropFilter: 'brightness(1.1)',
          color: 'inherit',
          maxHeight: 56,
        }}
        ref={ref}
      >
        <CardHeader
          avatar={
            <Avatar
              src={user.picture_url ?? undefined}
              alt={user.first_name ?? 'User Avatar'}
              sx={{ width: 31, height: 31 }}
            />
          }
          sx={{
            p: 1,
            transition: 'padding 0.3s ease',
            overflow: 'hidden',
          }}
          title={
            <TruncatedLabel
              text={`Welcome, ${user.first_name}`}
              suppressTooltip={open}
            />
          }
          subheader={
            <TruncatedLabel text={vendorName} suppressTooltip={open} />
          }
          slotProps={{
            content: {
              sx: {
                minWidth: 0,
                overflow: 'hidden',
              },
            },
            subheader: {
              variant: 'caption',
              color: 'inherit',
              opacity: 0.8,
              noWrap: true,
              sx: {
                display: collapsed ? 'none' : 'block',
                // The card is capped at 56px, so the two lines need a tighter
                // leading than caption's default to sit inside it.
                lineHeight: 1.4,
              },
            },
            title: {
              noWrap: true,
              sx: {
                display: collapsed ? 'none' : 'block',
              },
            },
          }}
        />
      </Card>
      <Menu
        id="basic-menu"
        anchorEl={ref.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={
          opensDownward
            ? {
                vertical: 'bottom',
                horizontal: 'left',
              }
            : {
                vertical: 'top',
                horizontal: 'left',
              }
        }
        keepMounted
        transformOrigin={
          opensDownward
            ? {
                vertical: 'top',
                horizontal: 'left',
              }
            : {
                vertical: 'bottom',
                horizontal: 'left',
              }
        }
        sx={{
          color: 'inherit',
        }}
        slotProps={{
          paper: {
            sx: {
              width: navCollapsed ? 'auto' : navWidth,
              backgroundColor: 'primary.light',
              color: 'primary.contrastText',
              mt: navMode === 'top' ? 1 : 0,
              mb: navMode === 'left' ? 2 : 0,
            },
          },
        }}
      >
        {dropdownItems.map((item) => {
          const isLinkItem = 'href' in item;
          const itemKey = isLinkItem
            ? `link-${(item as LinkDropdownItem).href}`
            : `action-${item.label}`;

          if (isLinkItem) {
            return (
              <MenuItem
                key={itemKey}
                component={Link}
                to={(item as LinkDropdownItem).href}
                onClick={closeMenuAndDrawer}
                sx={{ textDecoration: 'none', color: 'inherit' }}
              >
                <ListItemIcon sx={{ color: 'primary.contrastText' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText>{item.label}</ListItemText>
              </MenuItem>
            );
          }

          return (
            <MenuItem
              key={itemKey}
              onClick={() => {
                (item as FunctionDropdownItem).onClick();
                closeMenuAndDrawer();
              }}
            >
              <ListItemIcon sx={{ color: 'primary.contrastText' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          );
        })}
        <Divider sx={{ my: 1, mx: 0.5, borderColor: 'primary.main' }} />
        <MenuItem
          onClick={() => setMuiTheme(themeMode === 'light' ? 'dark' : 'light')}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              width: 1,
            }}
          >
            <Brightness4 fontSize="small" />
            <Switch
              checked={themeMode === 'light'}
              color="success"
              onChange={(evt) => {
                setMuiTheme(evt.target.checked ? 'light' : 'dark');
                evt.stopPropagation();
              }}
            />
            <LightMode fontSize="small" />
          </Stack>
        </MenuItem>

        {!isTablet && (
          <MenuItem
            onClick={() => {
              setNavMode(navMode === 'left' ? 'top' : 'left');
              setOpen(false);
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                width: 1,
              }}
            >
              <ViewSidebar
                fontSize="small"
                sx={{ transform: 'rotate(180deg)' }}
              />
              <Switch
                color="success"
                checked={navMode === 'top'}
                onChange={(evt) => {
                  setNavMode(evt.target.checked ? 'top' : 'left');
                  setOpen(false);
                  evt.stopPropagation();
                }}
              />
              <ViewSidebar
                fontSize="small"
                sx={{ transform: 'rotate(270deg)' }}
              />
            </Stack>
          </MenuItem>
        )}
        <Divider sx={{ my: 1, mx: 0.5, borderColor: 'primary.main' }} />
        <MenuItem
          onClick={() => {
            logoutFn();
            closeMenuAndDrawer();
          }}
        >
          <ListItemIcon sx={{ color: 'primary.contrastText' }}>
            <Logout />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
